import { useEffect, useMemo, useState } from 'react';
import { Group, Typography } from '@screentone/core';
import { JsonEditor } from 'json-edit-react';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { PublicationSettingUpdateInput } from 'data/generated/graphql';
import PublicationSettingsActionBar from 'features/publication-settings-common/PublicationSettingsActionBar';
import usePublicationSettingsLock from 'features/publication-settings-common/usePublicationSettingsLock';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings, useSavePublicationSetting } from 'hooks/publication-settings';
import { useJsonField } from 'hooks/useJsonField';

const PublicationSettingsRulesConfig = () => {
  const [currentPublicationSetting, setCurrentPublicationSetting] = useState<PublicationSettingUpdateInput | null>(
    null
  );
  const [hasChanged, setHasChanged] = useState(false);

  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  const { publishingFragment, lockIsReady, lockLoading, isPageLockingEnabled, handleLock, handleRelease } =
    usePublicationSettingsLock();

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, isLoading: isLoad, fetchStatus } = usePublicationSettings(publicationSettingsQueryVariables);
  const isLoading = isLoad && fetchStatus !== 'idle';

  // The save puts the saved settings in the cache, so the form reloads from them, with their new version.
  const { mutate, isLoading: isUpdating } = useSavePublicationSetting();

  const rulesConfig = useJsonField(data?.publicationSetting.rulesConfig);

  const jsonFields = useMemo(() => [rulesConfig], [rulesConfig]);

  useEffect(() => {
    if (data) {
      setCurrentPublicationSetting(data.publicationSetting);
    }
  }, [data]);

  const buildPublicationSettingInput = (): PublicationSettingUpdateInput => ({
    ...currentPublicationSetting!,
    rulesConfig: rulesConfig.parseValue()?.getJSON()
  });

  const handleSave = () => {
    if (currentPublicationSetting === null || !currentProperty) {
      return;
    }

    let newPublicationSetting: PublicationSettingUpdateInput;
    try {
      newPublicationSetting = buildPublicationSettingInput();
    } catch (err) {
      alertError(`Cannot save — fix the highlighted JSON field(s) first. (${(err as Error).message})`);
      return;
    }

    mutate(
      {
        publicationSettingUpdateInput: newPublicationSetting
      },
      {
        onSuccess: () => {
          setHasChanged(false);
          jsonFields.forEach((field) => field.clearError());
          alertSuccess('Publication settings saved successfully');
        },
        onError: (error) => {
          alertError(`Failed to save publication settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const { isReadOnly } = publishingFragment;

  return (
    <Group gap="lg" direction="column">
      {!currentPublicationSetting ? (
        <ResponsiveLoader />
      ) : (
        <>
          <Group.Item fullWidth>
            <PublicationSettingsActionBar
              onSave={handleSave}
              hasChanged={hasChanged}
              isSaving={isUpdating}
              isLoading={isLoading}
              publishingFragment={publishingFragment}
              lockIsReady={lockIsReady}
              isPageLockingEnabled={isPageLockingEnabled}
              lockLoading={lockLoading}
              onLock={handleLock}
              onRelease={handleRelease}
            />
          </Group.Item>

          <Group.Item fullWidth>
            <Typography variant="note">
              JSON configuration of mobile article rules consumed by the Pubcrawl pipeline.
            </Typography>
            <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
              <JsonEditor
                data={rulesConfig.parseValue()?.getJSON()}
                indent={2}
                setData={(value) => {
                  rulesConfig.handleChange(JSON.stringify(value));
                  setHasChanged(true);
                }}
                maxWidth="100%"
                collapse={3}
                restrictEdit={isReadOnly}
                restrictDelete={isReadOnly}
                restrictAdd={isReadOnly}
              />
            </Group>
          </Group.Item>
        </>
      )}
    </Group>
  );
};

export default PublicationSettingsRulesConfig;
